import { useCallback, useEffect, useRef, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { useAddressSelection } from "~/pages/clients/components/client-detail/purchase-order/hooks/address/useAddressSelection";
import { useDeliveryOptions } from "~/pages/clients/components/client-detail/purchase-order/hooks/delivery/useDeliveryOptions";
import { useOrderItems } from "~/pages/clients/components/client-detail/purchase-order/hooks/order/useOrderItems";
import { useOrderSubmission } from "~/pages/clients/components/client-detail/purchase-order/hooks/order/useOrderSubmission";
import { useFleteProduct } from "~/pages/clients/components/client-detail/purchase-order/hooks/useFleteProduct";
import { Client, SalesRequestDocument } from "~/types/client";
import { FLETE_PRODUCT_ID } from "~/utils/fleteUtils";

export const useOrderForm = (client: Client) => {
  const { toast } = useToast();

  // State for comments and internal notes
  const [comments, setComments] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [facturaObservations, setFacturaObservations] = useState("");

  // No-venta (muestra / traslado): the order moves stock but is not a sale.
  const [isNonSale, setIsNonSale] = useState(false);

  // State for documents
  const [documents, setDocuments] = useState<SalesRequestDocument[]>([]);

  // Use our new custom hooks
  const { selectedAddressId, setSelectedAddressId, selectedAddress } =
    useAddressSelection();

  const {
    orderItems,
    setOrderItems,
    total,
    editingItem,
    setEditingItem,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem,
    hasPriceBelowSuggested,
  } = useOrderItems();

  const {
    deliveryOption,
    setDeliveryOption,
    deliveryDate,
    setDeliveryDate,
    requestedDeliveryDateStart,
    setRequestedDeliveryDateStart,
    requestedDeliveryDateEnd,
    setRequestedDeliveryDateEnd,
  } = useDeliveryOptions();

  // FLETE (freight) product management
  const { fleteProduct, fleteRequired, addFleteToOrder } = useFleteProduct({
    selectedAddressId,
    deliveryState: selectedAddress?.state,
    orderItems,
    handleAddProduct,
  });

  // Track if FLETE has been auto-added to prevent duplicate additions
  const fleteAutoAddedRef = useRef(false);

  // Check if FLETE is already in order
  const hasFleteInOrder = orderItems.some(
    (item) => item.product.id === FLETE_PRODUCT_ID
  );

  // Auto-add FLETE when address requires it and FLETE is not in order
  useEffect(() => {
    // Only auto-add if:
    // 1. FLETE is required for this address
    // 2. FLETE is not already in order
    // 3. FLETE product has been loaded
    // 4. We haven't already auto-added in this session (to prevent loops)
    if (
      fleteRequired &&
      !hasFleteInOrder &&
      fleteProduct &&
      !fleteAutoAddedRef.current
    ) {
      fleteAutoAddedRef.current = true;
      addFleteToOrder();
    }

    // Reset the auto-add flag if FLETE is no longer required
    if (!fleteRequired) {
      fleteAutoAddedRef.current = false;
    }
  }, [fleteRequired, hasFleteInOrder, fleteProduct, addFleteToOrder]);

  // Wrapped handleRemoveItem that blocks FLETE removal when required
  const handleRemoveItemWithFleteProtection = useCallback(
    (itemId: string) => {
      // Find the item being removed
      const itemToRemove = orderItems.find((item) => item.id === itemId);

      // Check if this is the FLETE product and if it's required
      if (itemToRemove?.product.id === FLETE_PRODUCT_ID && fleteRequired) {
        toast({
          title: "No se puede eliminar FLETE",
          description:
            "El flete es obligatorio para entregas fuera de la Región Metropolitana, Valparaíso y O'Higgins.",
          variant: "destructive",
        });
        return;
      }

      // Otherwise, proceed with removal
      handleRemoveItem(itemId);
    },
    [orderItems, fleteRequired, handleRemoveItem, toast]
  );

  const {
    handleSaveOrder: originalHandleSaveOrder,
    isFormValid,
    isSubmitting,
    refetchOverdueUnlock,
  } = useOrderSubmission({
    client,
    selectedAddress,
    selectedAddressId,
    orderItems,
    total,
    comments,
    internalNotes,
    facturaObservations,
    deliveryOption,
    deliveryDate,
    requestedDeliveryDateStart,
    requestedDeliveryDateEnd,
    hasPriceBelowSuggested,
    documents,
    isNonSale,
  });

  // Wrap the handleSaveOrder to ensure it has the latest documents
  const handleSaveOrder = (
    onSuccess: (status?: "pending" | "prepaid") => void
  ) => {
    console.log(
      "useOrderForm.handleSaveOrder: Documents to be sent",
      documents
    );
    originalHandleSaveOrder(onSuccess);
  };

  // Handler for document updates
  const handleDocumentsUpdate = (updatedDocs: SalesRequestDocument[]) => {
    console.log("useOrderForm: Setting documents from handler", updatedDocs);
    setDocuments([...updatedDocs]);
  };

  // Log when documents change for debugging
  useEffect(() => {
    console.log("useOrderForm: Documents updated", documents);
  }, [documents]);

  // Reset form state
  const resetForm = () => {
    setOrderItems([]);
    setComments("");
    setInternalNotes("");
    setFacturaObservations("");
    setDeliveryOption("date");
    setDeliveryDate(undefined);
    setRequestedDeliveryDateStart(undefined);
    setRequestedDeliveryDateEnd(undefined);
    setEditingItem(null);
    setSelectedAddressId(null);
    setDocuments([]);
    setIsNonSale(false);
    fleteAutoAddedRef.current = false;
  };

  return {
    selectedAddressId,
    setSelectedAddressId,
    selectedAddress,
    orderItems,
    total,
    editingItem,
    comments,
    setComments,
    internalNotes,
    setInternalNotes,
    facturaObservations,
    setFacturaObservations,
    isNonSale,
    setIsNonSale,
    deliveryOption,
    setDeliveryOption,
    deliveryDate,
    setDeliveryDate,
    requestedDeliveryDateStart,
    setRequestedDeliveryDateStart,
    requestedDeliveryDateEnd,
    setRequestedDeliveryDateEnd,
    documents,
    setDocuments,
    resetForm,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem: handleRemoveItemWithFleteProtection,
    handleSaveOrder,
    isFormValid,
    isSubmitting,
    hasPriceBelowSuggested,
    // FLETE-related state
    fleteRequired,
    hasFleteInOrder,
    // Unlock refetch
    refetchOverdueUnlock,
  };
};
